<template>
  <div class="card mb-25 border-0 rounded-0 bg-white edit-emploi-du-temps-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="emploiDuTempsForm"
        @submit="editEmploiDuTemps"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="emploiDuTempsSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          typeEmploi: selectedTypeEmploi,
          dateDebut: dateDebut,
          dateFin: dateFin,
          niveau: selectedNiveau,
          filiere: selectedFiliere,
          coursAdded: addedCours
        }"
      >
        <!-- Informations générales sur une ligne -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type d'emploi du temps <span class="text-danger">*</span>
              </label>
              <Field name="typeEmploi" v-slot="{ field }">
                <Multiselect
                  v-bind="field"
                  :options="typeEmploiOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  placeholder="Sélectionner le type"
                />
              </Field>
              <ErrorMessage name="typeEmploi" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date début <span class="text-danger">*</span>
              </label>
              <Field name="dateDebut" type="date" :min="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateDebut" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date fin <span class="text-danger">*</span>
              </label>
              <Field name="dateFin" type="date" :value="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateFin" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Niveau en premier, puis Filière -->
        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  :options="globalNiveauxOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onGlobalNiveauChange"
                  placeholder="Sélectionner le niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Filière <span class="text-danger">*</span>
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  :options="globalFilieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onGlobalFiliereChange"
                  placeholder="Sélectionner la filière"
                  :disabled="!globalSelectedNiveau || isLoadingFilieres"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Section Cours -->
        <div class="row">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
                <div class="col-md-7">
                  <h4 class="fs-4 text-white">Modifier les cours</h4>
                </div>
                <div class="col-md-5">
                  <div class="d-flex float-end">
                    <button
                      class="btn btn-success"
                      type="button"
                      @click="addNewCours"
                    >
                      <i class="fa fa-plus-circle position-relative me-1 fs-12"></i>
                      Ajouter un cours
                    </button>
                  </div>
                </div>
              </div>

              <!-- Liste dynamique des cours -->
              <div class="marge-droite">
                <div v-for="(cours, index) in addedCours.filter(c => !c.toDelete)" :key="cours.tempId || cours.id">
                  <!-- Badge pour séparer chaque cours -->
                  <div class="row mt-10">
                    <div class="col-md-12">
                      <h5 class="badge bg-primary text-white fs-6 mb-10">
                        Cours {{ index + 1 }}
                      </h5>
                    </div>
                  </div>
                  
                  <!-- Première ligne : Jour, Heure début, Heure fin, Matière -->
                  <div class="row mt-10">
                    <div class="col-md-3">
                      <label class="d-block text-black fw-semibold mb-10">
                        Jour <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].jour`" v-slot="{ field }">
                        <Multiselect
                          v-bind="field"
                          :options="joursOptions"
                          :searchable="true"
                          :multiple="true"
                          mode="tags"
                          track-by="value"
                          label="label"
                          placeholder="Sélectionner jours"
                          @change="(value) => onJourChange(index, value)"
                          :value="field.value"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].jour`" class="text-danger"/>
                    </div>
                    <div class="col-md-2">
                      <label class="d-block text-black fw-semibold mb-10">
                        Heure de début <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].heureDebut`" type="time"
                        v-slot="{ field }"
                        class="form-control shadow-none text-black">
                        <input
                          v-bind="field"
                          type="time"
                          class="form-control shadow-none text-black"
                          @change="(event) => onHeureChange(index, 'debut', event)"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].heureDebut`" class="text-danger"/>
                    </div>
                    <div class="col-md-2">
                      <label class="d-block text-black fw-semibold mb-10">
                        Heure de fin <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].heureFin`" type="time"
                        v-slot="{ field }"
                        class="form-control shadow-none text-black">
                        <input
                          v-bind="field"
                          type="time"
                          class="form-control shadow-none text-black"
                          @change="(event) => onHeureChange(index, 'fin', event)"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].heureFin`" class="text-danger"/>
                    </div>
                    <div class="col-md-5">
                      <label class="d-block text-black fw-semibold mb-10">
                        Matière <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].filiereNiveauMatiere`" v-slot="{ field, value }">
                        <Multiselect
                          :options="globalMatieresOptions"
                          :searchable="true"
                          track-by="value"
                          label="label"
                          v-bind="field"
                          :value="value"
                          @change="(matiereValue) => onMatiereChange(index, matiereValue)"
                          placeholder="Sélectionner la matière"
                          :disabled="!globalSelectedNiveau || !globalSelectedFiliere || isLoadingMatieres"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].filiereNiveauMatiere`" class="text-danger"/>
                    </div>
                  </div>
                  
                  <!-- Deuxième ligne : Professeur, Classe, En ligne/Présentiel, Supprimer -->
                  <div class="row mt-10">
                      <div class="col-md-4">
                        <label class="d-block text-black fw-semibold mb-10">
                          Professeur <span class="text-danger">*</span>
                        </label>
                        <Field :name="`coursAdded[${index}].professeur`" v-slot="{ field }">
                          <Multiselect
                            :options="getProfesseursByMatiere(cours.matiereId)"
                            :searchable="true"
                            track-by="label"
                            label="label"
                            v-bind="field"
                            placeholder="Sélectionner le professeur"
                            :disabled="!cours.matiereId || !getProfesseursByMatiere(cours.matiereId).length"
                          />
                        </Field>
                        <ErrorMessage :name="`coursAdded[${index}].professeur`" class="text-danger"/>
                      </div>
                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-10">
                        Classe <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].classe`" v-slot="{ field }">
                        <Multiselect
                          :options="classesOptions"
                          :searchable="true"
                          track-by="label"
                          label="label"
                          v-bind="field"
                          placeholder="Sélectionner la classe"
                          @change="(classeValue) => onClasseChange(index, classeValue)"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].classe`" class="text-danger"/>
                    </div>
  
                    <div class="col-md-2 d-flex align-items-center mt-30">
                      <Field :name="`coursAdded[${index}].estEnLigne`" v-slot="{ field, handleChange }">
                        <div class="form-check">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :id="`enligne-${index}`"
                            :checked="field.value === true"
                            @change="handleChange"
                          />
                          <label class="form-check-label fw-semibold" :for="`enligne-${index}`">
                            En ligne
                          </label>
                        </div>
                      </Field>
                    </div>

                    <div class="col-md-2 d-flex align-items-start">
                      <button
                        class="btn btn-danger transition border-0 ps-8 pe-8 mt-30"
                        type="button"
                        @click="removeCours(index)"
                        v-if="addedCours.length > 0"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <!-- Indicateur de disponibilité de la classe -->
                <div 
                  v-if="getCoursDisponibilite(cours.tempId, cours.id) && !getCoursDisponibilite(cours.tempId, cours.id)?.available" 
                  class="row mt-10"
                >
                  <div class="col-md-12">
                    <div 
                      class="alert alert-danger d-flex align-items-center"
                      role="alert"
                    >
                      <i class="fas fa-exclamation-triangle me-2"></i>
                      Classe {{ getClasseLabel(cours.classe || null) }} occupée sur au moins un jour
                    </div>
                  </div>
                </div>
                </div>
              </div>
              <hr class="mt-0" />
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="row">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting "
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Modification en cours' : 'Modifier l\'emploi du temps' }}
              </button>
              <router-link
                to="/liste-emplois"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter, useRoute } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';

// Interfaces TypeScript explicites (mêmes que dans AddEmploiDuTemps)
interface Option {
  value: string | number;
  label: string;
}

interface CoursForm {
  id?: number;
  jour: string[];
  heureDebut: string;
  heureFin: string;
  filiereNiveauMatiere: number | null;
  professeur: number | null;
  classe: number | null;
  estEnLigne: boolean;
  matiereId: number | null;
  tempId?: number;
  toDelete?: boolean;
}

interface Disponibilite {
  available: boolean;
  checking?: boolean;
  conflictingDays?: string[];
  classeId?: number;
  error?: boolean;
}

interface AvailabilityResponse {
  available: boolean;
  hasConflict: boolean;
  conflictingCours: any | null;
  error?: boolean;
}

export default defineComponent({
  name: "EditEmploiDuTemps",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    // Schema de validation avec typage explicite (même que Add)
    const emploiDuTempsSchema = Yup.object().shape({
      typeEmploi: Yup.string().required("Le type d'emploi est obligatoire"),
      dateDebut: Yup.date()
        .required("La date de début est obligatoire")
        .min(new Date(new Date().setHours(0,0,0,0)), "La date de début ne peut pas être dans le passé"),
      dateFin: Yup.date()
        .required("La date fin est obligatoire")
        .min(
          Yup.ref("dateDebut"),
          "La date de fin doit être supérieure ou égale à la date de début"
        ),
      niveau: Yup.number().nullable().required("Le niveau est obligatoire"),
      filiere: Yup.number().nullable().required("La filière est obligatoire"),
      coursAdded: Yup.array().of(
        Yup.object().shape({
          jour: Yup.array().of(Yup.string()).min(1, "Veuillez sélectionner au moins un jour").required("Le jour est obligatoire"),
          heureDebut: Yup.string().required("L'heure de début est obligatoire"),
          heureFin: Yup.string()
            .required("L'heure de fin est obligatoire")
            .test(
              "heure-fin-apres-debut",
              "L'heure de fin doit être supérieure à l'heure de début",
              function(value) {
                const { heureDebut } = this.parent;
                if (!heureDebut || !value) return true; 
                return value > heureDebut;
              }
            ),
          filiereNiveauMatiere: Yup.number().nullable().required("La matière est obligatoire"),
          professeur: Yup.number().nullable().required("Le professeur est obligatoire"),
          classe: Yup.number().nullable().required("La classe est obligatoire"),
          estEnLigne: Yup.boolean().transform((value) => {
            if (value === "on" || value === true) return true;
            return false;
          }),
        })
      ).min(1, "Veuillez ajouter au moins un cours"),
    });

    const emploiDuTempsForm = ref<any>(null);
    const router = useRouter();
    const route = useRoute();
    
    // États pour l'emploi du temps existant
    const emploiDuTemps = ref<any>(null);
    const dateDebut = ref("");
    const dateFin = ref("");
    const selectedFiliere = ref<number | null>(null);
    const selectedNiveau = ref<number | null>(null);
    const selectedTypeEmploi = ref<number | null>(null);
    
    // Typage explicite pour toutes les options (mêmes que Add)
    const typeEmploiOptions = ref<Option[]>([]);
    const classesOptions = ref<Option[]>([]);
    const globalNiveauxOptions = ref<Option[]>([]);
    const globalFilieresOptions = ref<Option[]>([]);
    const globalMatieresOptions = ref<any[]>([]);
    const joursOptions = ref<Option[]>([
      { value: 'Lundi', label: 'Lundi' },
      { value: 'Mardi', label: 'Mardi' },
      { value: 'Mercredi', label: 'Mercredi' },
      { value: 'Jeudi', label: 'Jeudi' },
      { value: 'Vendredi', label: 'Vendredi' },
      { value: 'Samedi', label: 'Samedi' },
      { value: 'Dimanche', label: 'Dimanche' },
    ]);
    
    const globalSelectedNiveau = ref<number | null>(null);
    const globalSelectedFiliere = ref<number | null>(null);
    
    // Stockage des professeurs par matière
    const professeursParMatiere = ref<Map<number, Option[]>>(new Map());
    
    // Typage explicite pour addedCours
    const addedCours = ref<CoursForm[]>([]);
    const isSubmitting = ref(false);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);
    
    // Typage explicite pour coursDisponibilite
    const coursDisponibilite = ref<Record<number, Disponibilite>>({});
    
    let tempCoursIdCounter = 1;

    // Computed pour vérifier s'il y a des conflits de disponibilité
    const hasAvailabilityConflicts = computed(() => {
      return Object.values(coursDisponibilite.value).some((dispo: Disponibilite) => !dispo.available);
    });

    // Fonction pour récupérer les professeurs par matière
    const getProfesseursByMatiere = (matiereId: number | null): Option[] => {
      if (!matiereId || typeof matiereId !== 'number' || matiereId <= 0) {
        return [];
      }
      return professeursParMatiere.value.get(matiereId) || [];
    };

    // Fonction pour obtenir le label de la classe
    const getClasseLabel = (classeId: number | null): string => {
      if (!classeId || classeId === null) {
        return 'inconnue';
      }
      const classe = classesOptions.value.find(c => c.value === classeId);
      return classe ? classe.label : `ID ${classeId}`;
    };

    const handleInvalidSubmit = ({ errors }: any) => {
      console.error("Soumission invalide, erreurs :", JSON.stringify(errors, null, 2));
      error("Veuillez remplir les champs du formulaire");
    };

    const getCurrentDate = (): string => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

    onMounted(async () => {
      await Promise.all([
        fetchTypeEmploi(),
        fetchClasses(),
        fetchNiveaux(),
        fetchProfesseursParMatiere()
      ]);
      
      if (route.params.id) {
        await fetchEmploiDuTemps(parseInt(route.params.id as string));
      }
      
      // Ajouter un cours si aucun n'existe
      if (addedCours.value.length === 0) {
        addNewCours();
      }
    });

    const fetchEmploiDuTemps = async (id: number) => {
      try {
        const response = await ApiService.get(`/emplois-du-temps/${id}`);
        emploiDuTemps.value = response.data.data;
        const emploi = response.data.data;

        // Pré-remplir les champs du formulaire
        console.log("dates", emploi.dateDebut.split("T"),emploi.dateFin.split("T"))
        dateDebut.value = emploi.dateDebut.split("T")[0];
        dateFin.value = emploi.dateFin.split("T")[0];
        selectedTypeEmploi.value = emploi.typeEmploi?.id;
        selectedFiliere.value = emploi.filiere?.id;
        selectedNiveau.value = emploi.niveau?.id;
        
        globalSelectedNiveau.value = selectedNiveau.value;
        globalSelectedFiliere.value = selectedFiliere.value;

        // Mettre à jour les valeurs du formulaire
        await emploiDuTempsForm.value?.setFieldValue('dateDebut', dateDebut.value);
        await emploiDuTempsForm.value?.setFieldValue('dateFin', dateFin.value);
        await emploiDuTempsForm.value?.setFieldValue('typeEmploi', selectedTypeEmploi.value);
        
        await emploiDuTempsForm.value?.setFieldValue('niveau', selectedNiveau.value);

        // Charger les filières et matières pour le niveau et la filière sélectionnés
        if (selectedNiveau.value) {
          await fetchFilieresParNiveau(selectedNiveau.value);
        }

        await emploiDuTempsForm.value?.setFieldValue('filiere', selectedFiliere.value);
        
        if (selectedNiveau.value && selectedFiliere.value) {
          await fetchMatieresFiltered(selectedNiveau.value, selectedFiliere.value);
        }

        // Convertir les cours existants
        if (emploi.cours && emploi.cours.length > 0) {
          addedCours.value = emploi.cours.map((cours: any) => {
            const tempId = tempCoursIdCounter++;
            // Initialiser la disponibilité
            coursDisponibilite.value[tempId] = { available: true, checking: false };
            
            return {
              id: cours.id,
              jour: [cours.jour], // Convertir en array pour le multiselect
              heureDebut: cours.heureDebut,
              heureFin: cours.heureFin,
              filiereNiveauMatiere: cours.filiereNiveauMatiere?.id,
              professeur: cours.professeur?.id,
              classe: cours.classe?.id,
              estEnLigne: cours.estEnLigne || false,
              matiereId: cours.filiereNiveauMatiere?.matiere?.id,
              tempId: tempId,
              toDelete: cours.toDelete || false,
            } as CoursForm;
          });
          
          // Vérifier la disponibilité des cours existants
          for (let i = 0; i < addedCours.value.length; i++) {
            if (!addedCours.value[i].estEnLigne) {
              checkCoursAvailability(i);
            }
          }
        }

        await emploiDuTempsForm.value?.setFieldValue('coursAdded', addedCours.value);

      } catch (err: unknown) {
        console.error("Erreur lors de la récupération de l'emploi du temps", err);
        error("Erreur lors de la récupération de l'emploi du temps");
      }
    };

    const fetchTypeEmploi = async () => {
      try {
        const response = await ApiService.get('/typeEmploiDuTemps?limit=0');
        const typeEmploiData = response.data.data.data;
        typeEmploiOptions.value = typeEmploiData.map((type: any) => ({
          value: type.id,
          label: type.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des types d'emploi", err);
        error("Impossible de charger les types d'emploi");
      }
    };

    const fetchClasses = async () => {
      try {
        const response = await ApiService.get('/classes?limit=0');
        const classesData = response.data.data.data;
        classesOptions.value = classesData.map((classe: any) => ({
          value: classe.id,
          label: classe.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des classes", err);
      }
    };

    const fetchProfesseursParMatiere = async () => {
      try {
        console.log('Début du chargement des professeurs par matière...');
        
        // Récupérer toutes les matières disponibles
        const responseMatieres = await ApiService.get('/filiereNiveauMatieres?limit=0');
        
        if (responseMatieres.data?.code === 200 && Array.isArray(responseMatieres.data.data?.data)) {
          const toutesLesMatieres = responseMatieres.data.data.data;
          console.log(`${toutesLesMatieres.length} filiereNiveauMatieres trouvées`);
          
          // Extraire les IDs de matières uniques
          const matiereIdsSet = new Set<number>();
          toutesLesMatieres.forEach((fnm: any) => {
            if (fnm?.matiere?.id && typeof fnm.matiere.id === 'number' && fnm.matiere.id > 0) {
              matiereIdsSet.add(fnm.matiere.id);
            }
          });
          
          const matiereIds: number[] = Array.from(matiereIdsSet);
          console.log(`${matiereIds.length} matières uniques trouvées:`, matiereIds);

          if (matiereIds.length === 0) {
            console.warn('Aucune matière valide trouvée');
            return;
          }

          // Charger les professeurs pour chaque matière
          const professeurPromises = matiereIds.map(async (matiereId: number) => {
            try {
              console.log(`Chargement des professeurs pour la matière ${matiereId}...`);
              const responseProfesseurs = await ApiService.get(`/professeurs/matiere/${matiereId}?limit=0`);
              
              if (responseProfesseurs.data?.code === 200 && Array.isArray(responseProfesseurs.data.data?.data)) {
                const professeursData = responseProfesseurs.data.data.data;
                const professeursOptions = professeursData
                  .map((prof: any) => ({
                    value: Number(prof.id),
                    label: `${prof.nom || ''} ${prof.prenom || ''}`.trim(),
                    matiereId: matiereId,
                    email: prof.email,
                    npi: prof.npi
                  }))
                  .filter((prof: any) => prof.value > 0 && prof.label && prof.label !== '');
                
                professeursParMatiere.value.set(matiereId, professeursOptions);
                console.log(`✓ Matière ${matiereId}: ${professeursOptions.length} professeurs chargés`);
                return { matiereId, success: true, count: professeursOptions.length };
              } else {
                console.warn(`Réponse API invalide pour matière ${matiereId}:`, responseProfesseurs.data);
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: false, error: 'API response invalid' };
              }
            } catch (err: any) {
              console.error(`Erreur pour la matière ${matiereId}:`, err.message);
              
              if (err.response?.status === 404) {
                console.log(`Aucun professeur trouvé pour la matière ${matiereId}`);
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: true, count: 0, empty: true };
              }
              
              professeursParMatiere.value.set(matiereId, []);
              return { matiereId, success: false, error: err.message };
            }
          });

          const results = await Promise.all(professeurPromises);
          const successfulLoads = results.filter(r => r.success);
          const failedLoads = results.filter(r => !r.success);
          const emptyLoads = results.filter(r => r.empty);
          
          console.log(`Chargement terminé: ${successfulLoads.length} succès, ${emptyLoads.length} vides, ${failedLoads.length} échecs`);
          console.log('Map des professeurs:', professeursParMatiere.value);
          
        } else {
          console.warn('Réponse API matières invalide:', responseMatieres.data);
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des professeurs par matière", err);
      }
    };

    const fetchNiveaux = async () => {
      try {
        const response = await ApiService.get('/all/niveaux?limit=0');
        const niveauxData = response.data.data.data;
        globalNiveauxOptions.value = niveauxData.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des niveaux", err);
        error("Impossible de charger les niveaux");
      }
    };

    const fetchFilieresParNiveau = async (niveauId: number) => {
      if (!niveauId) {
        globalFilieresOptions.value = [];
        error("Veuillez sélectionner un niveau pour charger les filières.");
        return;
      }

      isLoadingFilieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        console.log('Réponse API filières par niveau:', response.data);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune filière trouvée pour ce niveau.");
            globalFilieresOptions.value = [];
            return;
          }
          const filieresUniques = Array.from(new Set(filiereNiveauMatieres.map((fnm: any) => fnm.filiere.id)))
            .map(id => filiereNiveauMatieres.find((fnm: any) => fnm.filiere.id === id).filiere);
          globalFilieresOptions.value = filieresUniques.map((filiere: any) => ({
            value: filiere.id,
            label: filiere.libelle,
          }));
        } else {
          globalFilieresOptions.value = [];
          error("Erreur lors de la récupération des filières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des filières filtrées", err);
        globalFilieresOptions.value = [];
        error("Impossible de charger les filières pour ce niveau.");
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    const fetchMatieresFiltered = async (niveauId: number, filiereId: number) => {
      if (!niveauId || !filiereId) {
        globalMatieresOptions.value = [];
        error("Veuillez sélectionner un niveau et une filière pour charger les matières.");
        return;
      }

      isLoadingMatieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&filiere=${filiereId}&limit=0`);
        console.log('Réponse API matières:', response.data);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune matière trouvée pour cette combinaison niveau-filière.");
            globalMatieresOptions.value = [];
            return;
          }
          globalMatieresOptions.value = filiereNiveauMatieres.map((fnm: any) => ({
            value: fnm.id,
            label: `${fnm.matiere.libelle}`,
            coefficient: fnm.coefficient,
            matiereId: fnm.matiere.id,
            matiere: fnm.matiere,
            niveauId: fnm.niveau.id,
            niveau: fnm.niveau,
            filiereId: fnm.filiere.id
          }));
          console.log('Matières avec coefficients:', globalMatieresOptions.value);
        } else {
          globalMatieresOptions.value = [];
          error("Erreur lors de la récupération des matières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des matières", err);
        globalMatieresOptions.value = [];
        error("Impossible de charger les matières.");
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    const onGlobalNiveauChange = async (niveauValue: any) => {
      globalSelectedNiveau.value = typeof niveauValue === 'object' ? niveauValue.value : niveauValue;
      console.log('Niveau sélectionné (ID):', globalSelectedNiveau.value);

      globalSelectedFiliere.value = null;
      globalFilieresOptions.value = [];
      globalMatieresOptions.value = [];
      await emploiDuTempsForm.value?.setFieldValue(`filiere`, null);

      // Réinitialiser les matières des cours existants
      addedCours.value.forEach(cours => {
        cours.filiereNiveauMatiere = null;
        cours.professeur = null;
      });

      if (globalSelectedNiveau.value) {
        await fetchFilieresParNiveau(globalSelectedNiveau.value);
      }
    };

    const onGlobalFiliereChange = async (filiereValue: any) => {
      globalSelectedFiliere.value = filiereValue;
      console.log('Filière sélectionnée:', filiereValue);
      
      if (filiereValue && globalSelectedNiveau.value) {
        await fetchMatieresFiltered(globalSelectedNiveau.value, filiereValue);
        
        // Réinitialiser les matières des cours existants
        addedCours.value.forEach(cours => {
          cours.filiereNiveauMatiere = null;
          cours.professeur = null;
        });
      }
    };

    const onMatiereChange = async (index: number, matiereValue: any) => {
      console.log(`Changement de matière pour le cours ${index}:`, matiereValue);

      if (!addedCours.value[index]) return;

      const selectedOption = globalMatieresOptions.value.find(opt => opt.value === matiereValue);

      addedCours.value[index].filiereNiveauMatiere = matiereValue;
      addedCours.value[index].matiereId = selectedOption?.matiereId ?? null;

      addedCours.value[index].professeur = null;
    };

    const onJourChange = (index: number, jours: Option[] | string[]) => {
      console.log(`Changement de jours pour le cours ${index}:`, jours);
      if (addedCours.value[index]) {
        if (Array.isArray(jours)) {
          if (jours.length > 0 && typeof jours[0] === 'object' && 'value' in jours[0]) {
            addedCours.value[index].jour = (jours as Option[]).map(j => j.value as string);
          } else {
            addedCours.value[index].jour = jours as string[];
          }
        } else {
          addedCours.value[index].jour = [jours as string];
        }
        
        console.log(`Jours mis à jour pour le cours ${index}:`, addedCours.value[index].jour);
        checkCoursAvailability(index);
      }
    };

    const onHeureChange = (index: number, type: 'debut' | 'fin', event: Event) => {
      const target = event.target as HTMLInputElement;
      const value = target.value;
      
      console.log(`Changement d'heure ${type} pour le cours ${index}:`, value);
      if (addedCours.value[index]) {
        if (type === 'debut') {
          addedCours.value[index].heureDebut = value;
        } else {
          addedCours.value[index].heureFin = value;
        }
        checkCoursAvailability(index);
      }
    };

    const onClasseChange = (index: number, classeValue: Option | number | null) => {
      console.log(`Changement de classe pour le cours ${index}:`, classeValue);
      
      if (!addedCours.value[index]) return;
      
      if (classeValue === null) {
        addedCours.value[index].classe = null;
        console.log(`Classe réinitialisée pour le cours ${index}`);
        checkCoursAvailability(index);
        return;
      }
      
      let classeId: number | null = null;
      
      if (typeof classeValue === 'number') {
        classeId = classeValue;
      } else if (typeof classeValue === 'object' && classeValue !== null && 'value' in classeValue) {
        classeId = (classeValue as Option).value as number;
      }
      
      addedCours.value[index].classe = classeId;
      console.log(`Classe définie pour le cours ${index}:`, classeId);
      
      if (classeId !== null) {
        checkCoursAvailability(index);
      }
    };

    const addNewCours = () => {
      const newCours: CoursForm = {
        jour: [],
        heureDebut: "",
        heureFin: "",
        filiereNiveauMatiere: null,
        professeur: null,
        classe: null,
        estEnLigne: false,
        matiereId: null,
        tempId: tempCoursIdCounter++,
      };
      addedCours.value.push(newCours);
      coursDisponibilite.value[newCours.tempId!] = { available: true, checking: false };
    };

    const removeCours = (index: number) => {
      const coursToRemove = addedCours.value[index];
      if (coursToRemove.id) {
        // Pour les cours existants, marquer pour suppression
        coursToRemove.toDelete = true;
      } else {
        // Pour les nouveaux cours, supprimer complètement
        addedCours.value.splice(index, 1);
        delete coursDisponibilite.value[coursToRemove.tempId!];
      }
    };

    const handleCheckboxChange = (index: number, event: Event) => {
      const target = event.target as HTMLInputElement | null;
      if (!target) return;
      addedCours.value[index].estEnLigne = target.checked;
      
      if (target.checked) {
        const coursId = addedCours.value[index].tempId || addedCours.value[index].id;
        if (coursId) {
          coursDisponibilite.value[coursId] = { available: true, checking: false };
        }
      } else {
        checkCoursAvailability(index);
      }
    };

    const checkCoursAvailability = async (index: number) => {
      const cours = addedCours.value[index];
      console.log(cours,"courscourscours")
      
      console.log(`Vérification cours ${index}:`, {
        cours: cours ? 'existe' : 'n\'existe pas',
        estEnLigne: cours?.estEnLigne,
        joursLength: cours?.jour?.length || 0,
        jours: cours?.jour,
        classe: cours?.classe,
        heureDebut: cours?.heureDebut,
        heureFin: cours?.heureFin
      });
      
      if (!cours || cours.estEnLigne || !cours.jour?.length || !cours.classe || !cours.heureDebut || !cours.heureFin) {
        const coursId = cours?.tempId || cours?.id || index;
        if (coursId) {
          coursDisponibilite.value[coursId] = { available: true, checking: false };
        }
        console.log(`Cours ${index} ignoré - conditions non remplies`);
        return;
      }

      const coursId = cours.tempId || cours.id;
      if (coursDisponibilite.value[coursId!]) {
        coursDisponibilite.value[coursId!].checking = true;
      } else {
        coursDisponibilite.value[coursId!] = { available: true, checking: true };
      }

      try {
        let hasConflict = false;
        const conflictingDays: string[] = [];

        for (const jour of cours.jour) {
          console.log(`Vérification pour le jour: "${jour}" (type: ${typeof jour})`);
          
          if (!jour || jour === 'undefined' || jour.trim() === '') {
            console.error(`Jour invalide détecté: "${jour}"`);
            continue;
          }
          
          const availability = await checkAvailability(
            cours.classe as number,
            jour.trim(),
            cours.heureDebut,
            cours.heureFin,
            parseInt(route.params.id.toString()),
            cours.professeur!
          );
          
          if (!availability.available) {
            hasConflict = true;
            conflictingDays.push(jour);
          }
        }

        coursDisponibilite.value[coursId!] = {
          available: !hasConflict,
          checking: false,
          conflictingDays: hasConflict ? conflictingDays : [],
          classeId: cours.classe
        };

        console.log(`Disponibilité cours ${index}:`, coursDisponibilite.value[coursId!]);
      } catch (err: unknown) {
        console.error(`Erreur vérification disponibilité cours ${index}:`, err);
        coursDisponibilite.value[coursId!] = { available: true, checking: false, error: true };
      }
    };

    const checkAvailability = async (
      classeId: number, 
      jour: string, 
      heureDebut: string, 
      heureFin: string,
      idemploiDuTemps? : number,
      professeurId? : number
    ): Promise<AvailabilityResponse> => {
      try {
        if (!classeId || !professeurId || !jour || !heureDebut || !heureFin) {
          console.error('Paramètres invalides:', { classeId, professeurId, jour, heureDebut, heureFin });
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        const cleanJour = jour.toString().trim();
        if (!cleanJour || cleanJour === 'undefined') {
          console.error('Jour invalide après nettoyage:', jour);
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        console.log('Vérification disponibilité - classe:', classeId, 'jour:', cleanJour, 'heures:', heureDebut, '-', heureFin);
        console.log('Vérification disponibilité - professeur:', professeurId, 'jour:', cleanJour, 'heures:', heureDebut, '-', heureFin);

        const response = await ApiService.get(
          `/check-classe-availability?classeId=${classeId}&jour=${encodeURIComponent(cleanJour)}&heureDebut=${heureDebut}&heureFin=${heureFin}&idemploiDuTemps=${idemploiDuTemps}&professeurId=${professeurId}&`
        );

        
        console.log('Résultat vérification:', response.data.data);
        return response.data.data as AvailabilityResponse;
      } catch (err: any) {
        console.error("Erreur lors de la vérification de disponibilité", err);
        return { available: false, hasConflict: true, conflictingCours: null, error: true };
      }
    };

  const editEmploiDuTemps = async (values: any, { resetForm }: any) => {
  isSubmitting.value = true;

  try {
    // ✅ Vérification de disponibilité avant soumission
    let finalAvailabilityCheck = true;
    const finalConflicts: string[] = [];

    for (let i = 0; i < values.coursAdded.length; i++) {
      const cours = values.coursAdded[i];
      if (cours.estEnLigne || cours.toDelete) continue;

      if (!cours.jour?.length || !cours.classe || !cours.heureDebut || !cours.heureFin) {
        finalConflicts.push(`Cours ${i + 1} : Données incomplètes`);
        finalAvailabilityCheck = false;
        continue;
      }

      for (const jour of cours.jour) {
        const availability = await checkAvailability(
          cours.classe ?? undefined,
          jour,
          cours.heureDebut,
          cours.heureFin,
          parseInt(route.params.id.toString()),
          cours.professeur ?? undefined
        );

        if (!availability.available) {
          finalConflicts.push(
            `Cours ${i + 1} : Conflit le ${jour} de ${cours.heureDebut} à ${cours.heureFin} ` +
            `dans la classe ${getClasseLabel(cours.classe)}`
          );
          finalAvailabilityCheck = false;
        }
      }
    }

    if (!finalAvailabilityCheck) {
      error(`Conflits de disponibilité détectés :\n${finalConflicts.join('\n')}`);
      return;
    }

    // ✅ Gestion des conflits internes
    const internalConflicts: string[] = [];
    values.coursAdded.forEach((cours: any, coursIndex: number) => {
      if (cours.estEnLigne || cours.toDelete) return;
      cours.jour.forEach((jour: string) => {
        values.coursAdded.forEach((otherCours: any, otherIndex: number) => {
          if (otherIndex === coursIndex || otherCours.estEnLigne || otherCours.toDelete) return;
          if (otherCours.classe === cours.classe && otherCours.jour.includes(jour)) {
            if (!(cours.heureFin <= otherCours.heureDebut || cours.heureDebut >= otherCours.heureFin)) {
              internalConflicts.push(
                `Conflit interne entre le cours ${coursIndex + 1} et le cours ${otherIndex + 1} ` +
                `le ${jour} dans la classe ${cours.classe}`
              );
            }
          }
        });
      });
    });

    if (internalConflicts.length > 0) {
      error(`Conflits internes détectés :\n${internalConflicts.join("\n")}`);
      return;
    }

    // ✅ Préparer le payload pour le backend
    const payload: any = {
      typeEmploi: values.typeEmploi,
      dateDebut: values.dateDebut,
      dateFin: values.dateFin,
      filiereId: typeof values.filiere === 'object' ? values.filiere.value : values.filiere,
      niveauId: typeof values.niveau === 'object' ? values.niveau.value : values.niveau
    };

    // 🔹 Mapping des cours
    const coursToCreate = values.coursAdded
      .filter((c: any) => !c.id && !c.toDelete)
      .flatMap((c: any) =>
        c.jour.map((j: string) => ({
          jour: j,
          heureDebut: c.heureDebut,
          heureFin: c.heureFin,
          filiereNiveauMatiereId: c.filiereNiveauMatiere ?? undefined,
          professeurId: c.professeur ?? undefined,
          classeId: c.classe ?? undefined,
          estEnLigne: !!c.estEnLigne
        }))
      );

    const coursToUpdate = values.coursAdded
      .filter((c: any) => c.id && !c.toDelete)
      .flatMap((c: any) =>
        c.jour.map((j: string) => ({
          id: c.id,
          jour: j,
          heureDebut: c.heureDebut,
          heureFin: c.heureFin,
          filiereNiveauMatiereId: c.filiereNiveauMatiere ?? undefined,
          professeurId: c.professeur ?? undefined,
          classeId: c.classe ?? undefined,
          estEnLigne: !!c.estEnLigne
        }))
      );

    const coursToDelete = values.coursAdded
      .filter((c: any) => c.toDelete && c.id)
      .map((c: any) => c.id);

    if (coursToCreate.length) payload.coursToCreate = coursToCreate;
    if (coursToUpdate.length) payload.coursToUpdate = coursToUpdate;
    if (coursToDelete.length) payload.coursToDelete = coursToDelete;

    console.log("Payload envoyé pour modification :", payload);

    // 🔹 Appel API
    const response = await ApiService.put(`/emplois-du-temps/${route.params.id}`, payload);

    if (response.data.code === 200) {
      success("Emploi du temps modifié avec succès !");
      resetForm();
      addedCours.value = [];
      globalSelectedNiveau.value = null;
      globalSelectedFiliere.value = null;
      globalFilieresOptions.value = [];
      globalMatieresOptions.value = [];
      coursDisponibilite.value = {};
      router.push('/liste-emplois');
    } else {
      throw new Error(response.data.message || "Erreur lors de la modification de l'emploi du temps");
    }

  } catch (err: any) {
    console.error("Erreur lors de la modification de l'emploi du temps:", err);
    if (err.response?.data?.message) {
      error(`Erreur: ${err.response.data.message}`);
    } else if (err.message) {
      error(err.message);
    } else {
      error("Impossible de modifier l'emploi du temps");
    }
  } finally {
    isSubmitting.value = false;
  }
};


    const debugCoursState = (index: number) => {
      const cours = addedCours.value[index];
      console.log(`=== DEBUG COURS ${index} ===`);
      console.log('Cours complet:', JSON.stringify(cours, null, 2));
      console.log('Jours type:', typeof cours?.jour);
      console.log('Jours isArray:', Array.isArray(cours?.jour));
      console.log('Jours contenu:', cours?.jour);
      console.log('Classe:', cours?.classe);
      console.log('Est en ligne:', cours?.estEnLigne);
      console.log('=========================');
    };

        // Ajoutez cette méthode helper
    const getCoursDisponibilite = (tempId?: number, id?: number) => {
      if (tempId) {
        return coursDisponibilite.value[tempId];
      }
      if (id) {
        return coursDisponibilite.value[id];
      }
      return undefined;
    };

    return {
      emploiDuTempsSchema,
      emploiDuTempsForm,
      typeEmploiOptions,
      classesOptions,
      globalNiveauxOptions,
      globalFilieresOptions,
      globalMatieresOptions,
      globalSelectedNiveau,
      globalSelectedFiliere,
      joursOptions,
      addedCours,
      getCoursDisponibilite,
      isSubmitting,
      hasAvailabilityConflicts,
      handleInvalidSubmit,
      getCurrentDate,
      addNewCours,
      debugCoursState,
      removeCours,
      editEmploiDuTemps,
      handleCheckboxChange,
      isLoadingMatieres,
      isLoadingFilieres, 
      onGlobalNiveauChange,
      onGlobalFiliereChange,
      getProfesseursByMatiere,
      onMatiereChange,
      onJourChange,
      onHeureChange,
      onClasseChange,
      coursDisponibilite,
      getClasseLabel,
      dateDebut,
      dateFin,
      selectedFiliere,
      selectedNiveau,
      selectedTypeEmploi,
    };
  },
});
</script>

<style scoped>
@import '@vueform/multiselect/themes/default.css';

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.alert {
  border-radius: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.border-primary {
  border-color: #007bff !important;
}

.border-success {
  border-color: #28a745 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-input.me-2 {
  margin-right: 0.75rem;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875em;
}

.badge.bg-primary {
  background-color: #007bff !important;
}

.badge.bg-success {
  background-color: #28a745 !important;
}

.mt-30 {
  margin-top: 30px;
}

.row.mt-10 {
  margin-top: 10px;
}

.alert {
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.marge-droite {
  padding-right: 15px;
  padding-left: 15px;
}
</style>